import React from 'react';
import { createStyles, FormLabel, Grid, makeStyles, TextField, Theme } from '@material-ui/core';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { difference } from 'lodash';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { date, mixed, object } from 'yup';

import { AlarmPriority, AlarmStatus, Site, getSiteEquipment, isAlarmTag } from '../../../models';
import { AlarmsPageQuery } from '../../../state/contexts/alarmsPage';

import { MultipleSelect } from '../../ui/controls/MultipleSelect';
import { TagTree } from '../site/TagTree';
import MainModal from 'src/components/ui/MainModal';
import ClearButton from 'src/components/ui/ClearButton';

import '../../ui/controls/dateTimePicker.css';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',

      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      padding: '0 8px',
      width: '50%',

      [theme.breakpoints.down('sm')]: {
        marginBottom: 16,
        padding: 0,
        width: '100%',
      },
    },
    selectMenu: {
      whiteSpace: 'normal',
    },
  })
);

export const QueryValidationSchema = object({
  start: date().required('Start date is required.'),
  end: date(),
  tagIds: mixed().test({
    name: 'contextIds',
    message: 'Must choose either tags or equipment to search by.',
    test: function (value) {
      const hasEquipmentIds = this.parent.equipmentIds && this.parent.equipmentIds.length > 0;
      const hasTagIds = value && value.length > 0;
      return hasTagIds || hasEquipmentIds;
    },
  }),
});

export interface Props {
  site: Site;
  currentQuery?: AlarmsPageQuery;
}

export interface DispatchProps {
  onCancel: () => void;
  onSubmitQuery: (query: AlarmsPageQuery) => void;
}

type AllProps = Props & DispatchProps;

const statusOptions = Object.values(AlarmStatus).map((s) => ({ name: s, value: s }));
const priorityOptions = Object.values(AlarmPriority).map((s) => ({ name: s, value: s }));

const filterDates = (dateOption: Date) => {
  return dateOption.getTime() < Date.now();
};

const dateFormat = 'yyyy-MM-dd h:mm aa';
const MAX_RANGE = 1000 * 60 * 60 * 24 * 90;

export const AlarmQueryModal: React.FC<AllProps> = (props) => {
  const classes = useStyles();

  const equipmentOptions = getSiteEquipment(props.site).map((equipment) => ({
    name: equipment.label,
    value: equipment.id,
  }));

  const handleSetStart = (newStart: Date, formikProps: FormikProps<AlarmsPageQuery>) => {
    const { end } = formikProps.values;
    if (end && end.getTime() - newStart.getTime() > MAX_RANGE) {
      const newEnd = new Date(newStart.getTime() + MAX_RANGE);
      formikProps.setFieldValue('start', newStart);
      formikProps.setFieldValue('end', newEnd);
    } else if (end && end < newStart) {
      formikProps.setFieldValue('start', newStart);
      formikProps.setFieldValue('end', new Date(newStart.getTime() + 1000 * 60 * 60 * 24));
    } else {
      formikProps.setFieldValue('start', newStart);
    }
  };

  const handleSetEnd = (newEnd: Date, formikProps: FormikProps<AlarmsPageQuery>) => {
    const { start } = formikProps.values;
    if (start && newEnd.getTime() - start.getTime() > MAX_RANGE) {
      const newStart = new Date(newEnd.getTime() - MAX_RANGE);
      formikProps.setFieldValue('start', newStart);
      formikProps.setFieldValue('end', newEnd);
    } else if (start && start > newEnd) {
      formikProps.setFieldValue('start', new Date(newEnd.getTime() - 1000 * 60 * 60 * 24));
      formikProps.setFieldValue('end', newEnd);
    } else {
      formikProps.setFieldValue('end', newEnd);
    }
  };

  return (
    <Formik
      validationSchema={QueryValidationSchema}
      onSubmit={props.onSubmitQuery}
      initialValues={
        props.currentQuery || {
          end: null as never,
          equipmentIds: [],
          priorities: [],
          start: null as never,
          statuses: [],
          tagIds: [],
        }
      }
      validateOnMount
    >
      {(formikProps) => (
        <MainModal
          disabledPrimaryButton={
            formikProps.isValidating || !formikProps.isValid || !formikProps.dirty
          }
          height={600}
          isOpen={true}
          message={formikProps.errors.tagIds}
          onClose={props.onCancel}
          onPrimaryAction={formikProps.handleSubmit}
          onSecondaryAction={props.onCancel}
          primaryButtonLabel="Query"
          secondaryButtonLabel="Cancel"
          title="Query Alarms"
          width={832}
          isSmall
          headerDivider={false}
        >
          <form>
            <div>
              <Grid container className={classes.container}>
                <Grid container item className={classes.content} spacing={2}>
                  <Grid item>
                    <Field name="start">
                      {({ field }: FieldProps<AlarmsPageQuery['start']>) => (
                        <DatePicker
                          selected={field.value}
                          onChange={(e: Date) => handleSetStart(e, formikProps)}
                          onBlur={field.onBlur}
                          dateFormat={dateFormat}
                          showTimeSelect
                          filterDate={filterDates}
                          customInput={
                            <TextField
                              fullWidth
                              label="Start Time"
                              variant="outlined"
                              placeholder="Enter start time"
                            />
                          }
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item>
                    <Field name="end">
                      {({ field }: FieldProps<AlarmsPageQuery['end']>) => (
                        <DatePicker
                          selected={field.value}
                          onChange={(e: Date) => handleSetEnd(e, formikProps)}
                          onBlur={field.onBlur}
                          dateFormat={dateFormat}
                          showTimeSelect
                          filterDate={filterDates}
                          customInput={
                            <TextField
                              fullWidth
                              label="End Time"
                              variant="outlined"
                              placeholder="Enter end time"
                            />
                          }
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item container direction="column">
                    <Grid item>
                      <Field name="statuses">
                        {({ field }: FieldProps<AlarmsPageQuery['statuses']>) => (
                          <MultipleSelect
                            fullWidth
                            variant="outlined"
                            options={statusOptions}
                            classes={{ selectMenu: classes.selectMenu }}
                            label="Status"
                            placeholder="Choose status"
                            size="medium"
                            {...field}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item>
                      {formikProps.values.statuses.length > 0 && (
                        <ClearButton onClick={() => formikProps.setFieldValue('statuses', [])} />
                      )}
                    </Grid>
                  </Grid>
                  <Grid item container direction="column">
                    <Grid item>
                      <Field name="priorities">
                        {({ field }: FieldProps<AlarmsPageQuery['priorities']>) => (
                          <MultipleSelect
                            fullWidth
                            variant="outlined"
                            classes={{ selectMenu: classes.selectMenu }}
                            options={priorityOptions}
                            label="Priority"
                            placeholder="Choose priority"
                            size="medium"
                            {...field}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item>
                      {formikProps.values.priorities.length > 0 && (
                        <ClearButton onClick={() => formikProps.setFieldValue('priorities', [])} />
                      )}
                    </Grid>
                  </Grid>
                  <Grid item container direction="column">
                    <Grid item>
                      <Field name="equipmentIds">
                        {({ field }: FieldProps<AlarmsPageQuery['equipmentIds']>) => (
                          <MultipleSelect
                            fullWidth
                            variant="outlined"
                            classes={{ selectMenu: classes.selectMenu }}
                            options={equipmentOptions}
                            label="Equipment"
                            placeholder="Choose equipment"
                            size="medium"
                            {...field}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item>
                      {formikProps.values.equipmentIds.length > 0 && (
                        <ClearButton
                          onClick={() => formikProps.setFieldValue('equipmentIds', [])}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item className={classes.content} container>
                  <Grid item xs={12}>
                    <FormLabel
                      style={{
                        marginBottom: 6,
                        display: 'block',
                        fontWeight: 500,
                        color: 'black',
                      }}
                    >
                      Tags
                    </FormLabel>
                    <Field name="tagIds">
                      {({ form }: FieldProps<AlarmsPageQuery>) => (
                        <TagTree
                          height={460}
                          site={props.site}
                          styleContent={{ padding: 0, marginTop: 8 }}
                          tagFilter={isAlarmTag}
                          onTagSelect={(tagId) => {
                            const next = form.values.tagIds.includes(tagId)
                              ? difference(form.values.tagIds, [tagId])
                              : [...form.values.tagIds, tagId];
                            form.setFieldValue('tagIds', next);
                          }}
                          selectedTags={form.values.tagIds}
                        />
                      )}
                    </Field>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </form>
        </MainModal>
      )}
    </Formik>
  );
};
